// Lib
import { isEmpty, chunk, assign, union, compact } from 'lodash';

// Util
import { http } from '../utils/services/http';
import { getElement } from '../../common/elements/utils/elementTraversalUtils';
import { isSkeleton } from '../../common/elements/utils/elementTypeUtils';
import { asyncResource } from '../utils/services/http/asyncResource/asyncResource';
import { getDataExpiryTimestamp } from '../utils/services/http/axiosResponseUtils';

// Actions
import { loadElements } from './actions/elementActions';
import { commentsLoad } from './comment/store/commentActions';
import { getTokenElementIdGroupsThunk } from '../utils/permissions/permissionsActions';

// State selectors
import { getElements } from './selectors/elementSelector';

// Constants
import { ResourceTypes } from '../utils/services/http/asyncResource/asyncResourceConstants';
import { TIMES } from '../../common/utils/timeUtil';
import { MNComment } from '../../common/comments/commentTypes';
import { MNElement } from '../../common/elements/elementModelTypes';

type TokenGroup = {
    token: string;
    elementIds: string[];
};

/**
 * Ensures that each group of elements is less than
 */
const getTokenElementBatches = (tokenGroups: TokenGroup[]): TokenGroup[] => {
    const groups: TokenGroup[] = [];

    tokenGroups.forEach(({ token, elementIds }) => {
        const elementIdBatches = chunk(elementIds, 200);

        elementIdBatches.forEach((elementIdBatch) => {
            if (isEmpty(compact(elementIdBatch))) return;
            groups.push({ token, elementIds: elementIdBatch });
        });
    });

    return groups;
};

type FetchElementsArgs = {
    elementIds: string[];
    force?: boolean;
    loadAncestors?: boolean;
    permissionIdsOverride?: Array<string | undefined>;
};

export type FetchElementsResponse = {
    comments: Record<string, MNComment>;
    elements: Record<string, MNElement>;
    elementCount: number;
    errors: Record<string, any>;
    ids: string[];
    asyncResource: {
        errors: Record<string, any>;
        expiry?: number;
    };
};

/**
 * Fetch multiple elements from the server.
 */
export const fetchElements =
    (args: FetchElementsArgs) =>
    async (dispatch: Function): Promise<FetchElementsResponse> =>
        dispatch(
            asyncResource(
                ResourceTypes.elements,
                args.elementIds,
                args.force,
            )(async (elementIdsToFetch) => {
                const { loadAncestors, permissionIdsOverride } = args;

                const tokenGroups = await dispatch(
                    getTokenElementIdGroupsThunk({ elementIds: elementIdsToFetch, permissionIdsOverride }),
                );
                const batches = getTokenElementBatches(tokenGroups);

                const responses = await Promise.all(
                    batches.map((batch) =>
                        http({
                            url: 'elements',
                            timeout: 30 * TIMES.SECOND,
                            params: {
                                loadAncestors,
                                tokens: batch.token || undefined,
                                ids: batch.elementIds.join(','),
                            },
                        }),
                    ),
                );

                let commentsMap = {};
                let elementsMap = {};
                let errorsMap = {};
                let count = 0;
                let allIds: string[] = [];

                let expiry: number | undefined = undefined;

                responses.forEach((response) => {
                    const { data = {} } = response;
                    const { comments = {}, elements = {}, elementCount = 0, errors = {}, ids = [] } = data;

                    commentsMap = assign(commentsMap, comments);
                    elementsMap = assign(elementsMap, elements);
                    errorsMap = assign(errorsMap, errors);
                    count += elementCount;
                    allIds = union(allIds, ids);

                    const responseExpiry = getDataExpiryTimestamp(response);

                    if (responseExpiry && (!expiry || responseExpiry < expiry)) {
                        expiry = responseExpiry;
                    }
                });

                !isEmpty(elementsMap) && dispatch(loadElements(elementsMap));

                !isEmpty(commentsMap) && dispatch(commentsLoad({ comments: commentsMap }));

                return {
                    comments: commentsMap,
                    elements: elementsMap,
                    elementCount: count,
                    errors: errorsMap,
                    ids: allIds,
                    asyncResource: {
                        errors: errorsMap,
                        expiry,
                    },
                };
            }),
        );

const createDebouncedFetchElements = (debounceTime = 250) => {
    let fetchTimeout: ReturnType<typeof setTimeout>;
    let pendingArgs: FetchElementsArgs[] = [];
    let pendingPromises: { resolve: (value: FetchElementsResponse) => void; reject: (reason?: any) => void }[] = [];

    return (args: FetchElementsArgs) =>
        async (dispatch: Function): Promise<FetchElementsResponse> =>
            new Promise((resolve, reject) => {
                pendingArgs.push(args);
                pendingPromises.push({ resolve, reject });

                clearTimeout(fetchTimeout);

                fetchTimeout = setTimeout(async () => {
                    const currentPendingPromises = [...pendingPromises];

                    const elementIds = pendingArgs.flatMap((arg) => arg.elementIds);

                    // The permission ID override relies on the index of the element IDs,
                    //  so if we're not overriding, we add an undefined for the element ID's index
                    const permissionIdsOverride = pendingArgs.flatMap(
                        (arg) => arg.permissionIdsOverride || arg.elementIds.map(() => undefined),
                    );

                    const elementIdsToPermissionIds = elementIds.reduce((acc, elementId, index) => {
                        const permissionId = permissionIdsOverride[index];
                        acc[elementId] = acc[elementId] || permissionId;
                        return acc;
                    }, {} as Record<string, string | undefined>);
                    const uniqueElementIds = Object.keys(elementIdsToPermissionIds);

                    const mergedArgs: FetchElementsArgs = {
                        elementIds: uniqueElementIds,
                        force: pendingArgs.some((arg) => arg.force),
                        loadAncestors: pendingArgs.some((arg) => arg.loadAncestors),
                        permissionIdsOverride: uniqueElementIds.map((id) => elementIdsToPermissionIds[id]),
                    };

                    // Clear state before processing to prevent memory leaks
                    pendingArgs = [];
                    pendingPromises = [];

                    try {
                        const result = await dispatch(fetchElements(mergedArgs));
                        // Resolve all pending promises with the result
                        currentPendingPromises.forEach(({ resolve }) => resolve(result));
                    } catch (err) {
                        // Reject all pending promises with the error
                        currentPendingPromises.forEach(({ reject }) => reject(err));
                    }
                }, debounceTime);
            });
};

/**
 * Triggers a multi-fetch elements call by debouncing invocations and collating the element IDs of
 * each invocation.
 */
export const debouncedFetchElements = createDebouncedFetchElements();

/**
 * Similar to fetchElements however if any of the elements are skeletons it will force fetch them.
 */
// FIXME-PERMISSIONS - This is a bit of a hack to get around the issue when users are currently viewing
//  boards that this user has already visited with a permissionId. A better way might be to pre-fetch
//  permission IDs.
export const fetchElementsForceSkeletons =
    (args: FetchElementsArgs) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const { elementIds, force } = args;

        if (force) return dispatch(fetchElements(args));

        const state = getState();

        const elements = getElements(state);

        const overriddenForce = elementIds.some((elementId) => {
            const element = getElement(elements, elementId);
            return isSkeleton(element);
        });

        return dispatch(fetchElements({ ...args, force: overriddenForce }));
    };
